import React, { useState, useEffect, useRef } from 'react';
import Axios from 'axios';
import { useSelector } from 'react-redux';
import { buttons, icons } from 'seal-ui-components';
import { CopyToClipboard } from 'react-copy-to-clipboard';
import ReactTooltip from 'react-tooltip';
import moment from 'moment';
import { NOTIFICATION_HANDLER, CONTRACT_SEARCH, MIX_PANEL } from '@Constants/types';
import { SingleSelectDropdown } from '@Components/UI/Dropdowns';
import { LOG_TYPE, addLog } from '@Service/loggingService';
import { STORAGE_TYPE, getFromStorage } from '@Service/storageService';
import {
    isWordOnlineWithIE11,
    isMSWordForWindows,
    isMacintosh,
    isWordonline,
    isIE11Browser,
    isEdgiumBrowser
} from '@Utils/commonUtils';
import { getFileExtension, getFilenameWithoutExtension } from '@Utils/fileUtils';
import {
    checkGenerationJobStatus,
    generateExportedDocx,
    getDocxDownloadPath
} from '@Actions/contractsActions';
import store from '@Store';
import { ContractsStatus } from '@Constants/status';
import {
    Title,
    PartyItem,
    GenerateExportedDocxButtonWrapper,
    SpinnerWrapper,
    ErrorText,
    CancelButtonWrapper,
    DownoladButtonWrapper,
    DownloadDialogWrapper,
    FileName,
    LinkButtonWrapper,
    ModalDialogFooter
} from './DownloadDialog.style';
import Worker from '@Workers/binaryDownloader.worker';
import mixpanelEvents from '@Mixpanel';
import { LinkIcon } from '@Components/UI/Icons';

const worker = new Worker();

let options = [{ key: 'original', text: 'Original Document' }];

const DownloadDialog = ({ docId, popupClosed, contractIndex }) => {
    const {
        exportedDocxExists,
        selectedContractLoadedState,
        contractingParties,
        uploadedByUsername,
        selectedFilename,
        selectedDocImportedAt,
        permaLinkOriginal,
        permaLinkDOCX,
        exportedDocxDownloadPath,
        originalDocxDownloadPath,
        generationJobId,
        documentStatus,
        generationError
    } = useSelector(state => state.contract);

    const [isOriginalCopied, setIsOriginalCopied] = useState(false);
    const [isDOCXCopied, setIsDOCXCopied] = useState(false);
    const [selectedItem, setSelectedItem] = useState('original');
    const [selectedValue, setSelectedValue] = useState('Original Document');
    const [buttonText, setButtonText] = useState('');
    const [isDownloading, setIsDownloading] = useState(false);
    const [isExporting, setIsExporting] = useState(false);
    const [genError, setGenError] = useState('');
    const interval = useRef();

    let browserDownload = false;
    let showCopyToClipboardButton = false;
    let binaryDownload = false;

    const isMSWordForWin32 = isMSWordForWindows();
    const isWOLWithIE11 = isWordOnlineWithIE11();
    const isMac = isMacintosh();
    const isWOL = isWordonline();
    const isIE11 = isIE11Browser();

    let fileExtension = getFileExtension(selectedFilename);
    const filenameWithoutExt = getFilenameWithoutExtension(selectedFilename);
    let filename = `${filenameWithoutExt}.docx`; // ensure that the filename has a .docx extension, exportedDocx or otherwise

    const raiseMixpanelEventDownloadDocument = () => {
        if (selectedItem === 'docx') {
            const mixpanelContracts = {
                fileType: 'docx'
            };

            store.dispatch({
                type: MIX_PANEL.MIXPANEL_DATA_CONTRACTS,
                payload: mixpanelContracts
            });

            mixpanelEvents.searchcontractdownloadcontract();
        }

        if (selectedItem === 'original') {
            const mixpanelContracts = {
                fileType: 'other'
            };

            store.dispatch({
                type: MIX_PANEL.MIXPANEL_DATA_CONTRACTS,
                payload: mixpanelContracts
            });

            mixpanelEvents.searchcontractdownloadcontract();
        }
    };

    const close = () => {
        options = [{ key: 'original', text: 'Original Document' }];
        store.dispatch({ type: CONTRACT_SEARCH.EXPORTED_DOCX_GENERATION_RESET });
        popupClosed();
    };

    const downloadOrOpenButtonText = docType => {
        fileExtension = getFileExtension(selectedFilename);

        if (isWOLWithIE11) {
            setButtonText('Download');
            return;
        }

        if (isMSWordForWin32) {
            if (
                docType === 'docx' ||
                (docType === 'original' && fileExtension.toLowerCase() === 'docx')
            ) {
                setButtonText('Open in Word');
                return;
            }

            setButtonText('Download');
            return;
        }

        setButtonText('Download');
    };

    const addExportedDocxOptionAndSelect = () => {
        if (options.length === 1) {
            options = [
                { key: 'original', text: 'Original Document' },
                { key: 'docx', text: 'Exported DOCX' }
            ];

            // default to the exported docx version
            setSelectedItem('docx');
            downloadOrOpenButtonText('docx');
        }
    };

    const checkForSubmitError = () => {
        if (generationError) {
            setIsExporting(false);
            setGenError(`Error generating exported docx. Technical Details: ${generationError}`);
        }

        if (!generationJobId) {
            setTimeout(() => checkForSubmitError(), 3000);
        }
    };

    const generatedExportedDocx = () => {
        mixpanelEvents.searchcontractexporttodocx();
        setGenError('');
        setIsExporting(true);
        generateExportedDocx(docId);
        checkForSubmitError();
    };

    const checkStatus = docStatus => {
        if (docStatus === 'Pending') {
            checkGenerationJobStatus(generationJobId);
        }

        if (docStatus === 'Processed') {
            store.dispatch(getDocxDownloadPath(docId)); // go fetch the downloadPath for this newly created document
            addExportedDocxOptionAndSelect();
            setIsExporting(false);
        }

        if (generationError) {
            setIsExporting(false);
            setGenError(`Error generating exported docx. Technical Details: ${generationError}`);
        }
    };

    useEffect(() => {
        mixpanelEvents.searchcontractclickoncontract();
    }, []);

    useEffect(
        () => {
            checkStatus(documentStatus);
            if (documentStatus === 'Pending')
                interval.current = setInterval(checkStatus, 3000, documentStatus);
            else if (documentStatus === 'Processed') clearInterval(interval.current);
        },
        [generationJobId, documentStatus]
    );

    const consumePermaLink = () => {
        if (selectedItem === 'docx') {
            setIsDOCXCopied(true);
        }

        if (selectedItem === 'original') {
            setIsOriginalCopied(true);
        }
    };

    const consumeDownloadButtonLink = () => {
        raiseMixpanelEventDownloadDocument();
        consumePermaLink();
    };

    const consumeClipboardLink = () => {
        mixpanelEvents.searchcontractcopydownloadlink();
        consumePermaLink();
    };

    const openNewDocument = data => {
        /* global Word */

        Word.run(context => {
            const myNewDoc = context.application.createDocument(data);
            context.load(myNewDoc);
            return context.sync().then(() => {
                context.sync();
                myNewDoc.open();
            });
        });
    };

    const determineDownloadButton = () => {
        /*  Evaluate the following variables to determine which type of download button to render...
            showCopyToClipboardButton=true  : show the copyToClipboard button instead of the [Download] button
            browserDownload=true            : use the browser's built-in download mechanism
            browserDownload=false           : display the binary data download button
            */

        // All Mac variants can only download documents
        if (isMac) {
            // console.log('isMac - use the browsers built-in download mechanism');
            browserDownload = true; // use the browser's built-in download mechanism
            binaryDownload = false;
            showCopyToClipboardButton = false;
            return;
        }

        // WordOnline-Chrome/Chromium/Edge/Safari can only download documents
        if (!isIE11 && isWOL) {
            // console.log('WordOnline-Chrome/Chromium/Edge/Safari can only download documents (regardless of fileType) - use the browsers built-in download mechanism');
            browserDownload = true; // use the browser's built-in download mechanism
            binaryDownload = false;
            showCopyToClipboardButton = false;
            return;
        }

        // WordOnline-IE11 - use the FileSaver.js to download the document
        if (isIE11 && isWOL) {
            // console.log('WordOnline-IE11 - use the FileSaver.js to download the document - binary data download + FileSaver.js');
            browserDownload = false;
            binaryDownload = true; // FileSaver.js can download/open any fileType
            showCopyToClipboardButton = false;
            return;
        }

        // if installed MSWord (for Windows) and original is selected and original IS a docx
        if (
            !isWOL &&
            !isMac &&
            selectedItem === 'original' &&
            fileExtension.toLowerCase() === 'docx'
        ) {
            // console.log('if installed MSWord (for Windows) and original is selected and original IS a docx - binary data download');
            browserDownload = false;
            binaryDownload = true; // binary data download and open (docx only)
            showCopyToClipboardButton = false;
            return;
        }

        // if installed MSWord (for Windows) and exportedDocx is selected
        if (!isWOL && !isMac && selectedItem === 'docx') {
            // console.log('if installed MSWord (for Windows) and exportedDocx is selected - binaryDownload')
            browserDownload = false;
            binaryDownload = true; // binary data download and open (docx only)
            showCopyToClipboardButton = false;
            return;
        }

        // if installed MSWord-Edgium (for Windows) and original is selected and original is NOT a docx
        if (
            !isWOL &&
            !isMac &&
            selectedItem === 'original' &&
            fileExtension.toLowerCase() !== 'docx' &&
            isEdgiumBrowser()
        ) {
            // console.log('if installed MSWord-Edgium (for Windows) and original is selected and original is NOW a docx  - ');
            browserDownload = true; // use the browser's built-in download mechanism
            binaryDownload = false;
            showCopyToClipboardButton = false;
            return;
        }

        // if installed MSWord (for Windows) and original is selected and original is NOT a docx
        if (
            !isWOL &&
            !isMac &&
            selectedItem === 'original' &&
            fileExtension.toLowerCase() !== 'docx'
        ) {
            // console.log('if installed MSWord (for Windows) and original is selected and original is NOW a docx  - showCopyToClipboardButton');
            browserDownload = false;
            binaryDownload = false;
            showCopyToClipboardButton = true; // default to show the copyToClipboard button as that is all we can do without an exportedDocx
            return;
        }

        // console.log('catch all - default to show the copyToClipboard button as that is all we can do without an exportedDocx');
        browserDownload = false;
        binaryDownload = false;
        showCopyToClipboardButton = true; // default to show the copyToClipboard button as that is all we can do without an exportedDocx
    };

    const downloadAndOpen = () => {
        raiseMixpanelEventDownloadDocument();
        /* eslint-disable prettier/prettier */
        /* eslint-disable no-bitwise  */
        const authToken = getFromStorage('authToken', STORAGE_TYPE.LOCAL_STORAGE);
        const authType = getFromStorage('authType', STORAGE_TYPE.LOCAL_STORAGE);

        let downloadPath = '';
        if (selectedItem === 'docx') {
            downloadPath = exportedDocxDownloadPath;
        }

        if (selectedItem === 'original') {
            if (fileExtension.toLowerCase() === 'docx' && originalDocxDownloadPath) {
                downloadPath = originalDocxDownloadPath;
            } else {
                downloadPath = permaLinkOriginal;
                filename = selectedFilename;
            }
        }

        if (isWOLWithIE11) {
            setIsDownloading(true);

            Axios({
                url: downloadPath,
                method: 'GET',
                responseType: 'blob',
                headers: {
                    ...(authType === 'basic' || authType === 'SAML'
                        ? { 'X-Session-Token': authToken }
                        : { Authorization: authToken })
                }
            })
                .then(response => {
                    /* eslint-disable global-require */

                    // for IE 11, we can save to disk this way

                    const FileSaver = require('file-saver');
                    FileSaver.saveAs(response.data, filename);
                    setIsDownloading(false);
                })
                .catch(e => {
                    setIsDownloading(false);

                    addLog(LOG_TYPE.ERROR, 'DownloadDialog.downloadAndOpen', e);
                    store.dispatch({
                        type: NOTIFICATION_HANDLER.WARNING,
                        payload: 'failed to download the document IE'
                    });
                });
        } else {
            if (!exportedDocxDownloadPath && !originalDocxDownloadPath) {
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload: 'DOCX version unavailable'
                });

                return;
            }

            setIsDownloading(true);

            worker.postMessage({
                authToken,
                authType,
                downloadPath,
                isEdgium: isEdgiumBrowser()
            });

            worker.onmessage = e => {
                const data = e.data;

                // Installed MSWord-Edge/IE11 download and open in new instance of MSWord

                openNewDocument(data);

                setIsDownloading(false);
            };

            worker.onerror = e => {
                setIsDownloading(false);
                addLog(LOG_TYPE.ERROR, 'DownloadDialog.downloadAndOpen', e);
                store.dispatch({
                    type: NOTIFICATION_HANDLER.WARNING,
                    payload: 'failed to download the document'
                });
            };
        }
    };

    // Add the "Exported DOCX" dropdown menu option when the async endpoint returns
    // the results of whether or not an exported docx exists
    useEffect(
        () => {
            if (exportedDocxExists && options.length === 1) {
                options = [
                    { key: 'original', text: 'Original Document' },
                    { key: 'docx', text: 'Exported DOCX' }
                ];

                // default to the exported docx version
                setSelectedItem('docx');
                setSelectedValue('Exported DOCX');
                downloadOrOpenButtonText('docx');
                return;
            }

            downloadOrOpenButtonText(selectedItem);
            determineDownloadButton();
        },
        [exportedDocxExists]
    );

    const onChange = item => {
        setSelectedItem(item.key);
        setSelectedValue(item.text);
        downloadOrOpenButtonText(item.key);
        determineDownloadButton();
    };

    const utcDate = moment(selectedDocImportedAt, 'YYYY-MM-DDTHH:mm:ss.SSSZ');
    const localDate = utcDate.local();
    const importedAt = localDate.format('YYYY-DD-MM HH:mm:ss');

    determineDownloadButton();

    let isDisabled =
        !isMSWordForWin32 &&
        !isWOLWithIE11 &&
        (selectedContractLoadedState !== ContractsStatus.LOADED ||
            (selectedItem === 'docx' && isDOCXCopied) ||
            (selectedItem === 'original' && isOriginalCopied));

    if (binaryDownload && isDownloading) {
        isDisabled = true;
    }

    let showExportSection = false;
    if (selectedContractLoadedState === ContractsStatus.LOADED) {
        if (fileExtension.toLowerCase() !== 'docx') {
            if (selectedItem !== 'docx') {
                if (exportedDocxExists === false) {
                    showExportSection = true;
                }
            }
        }
    }

    if (documentStatus === 'Processed') {
        showExportSection = false;
    }

    return (
        <DownloadDialogWrapper>
            <Title>FILENAME</Title>
            <FileName data-testid={`contract_${contractIndex}_filename`}>
                {selectedFilename}
            </FileName>
            <Title>IMPORTED AT</Title>
            <span data-testid={`contract_${contractIndex}_imported_at`}>{importedAt}</span>
            {/** if the contract's status is "Loaded", but the user name is "", then it means
             * the current user has no permission to view the name of the user who uploaded
             * the contract and the "Uploaded by" field is removed all together
             */}
            {selectedContractLoadedState === ContractsStatus.LOADED &&
                uploadedByUsername && <Title>UPLOADED BY</Title>}
            {selectedContractLoadedState !== ContractsStatus.LOADED ? (
                <div>
                    <icons.LoadingSmallIcon data-testid="loading-uploaded_by" />
                </div>
            ) : (
                <span data-testid={`contract_${contractIndex}_uploaded_by`}>
                    {uploadedByUsername}
                </span>
            )}
            {showExportSection && (
                <div>
                    <Title>EXPORT</Title>
                    <GenerateExportedDocxButtonWrapper>
                        <buttons.Button
                            size="medium"
                            appearance="secondary"
                            disabled={isExporting}
                            onClick={generatedExportedDocx}
                        >
                            Generate Docx Version
                        </buttons.Button>
                    </GenerateExportedDocxButtonWrapper>
                    {isExporting && (
                        <SpinnerWrapper>
                            <icons.LoadingSmallIcon data-testid="loading-exporting" />
                        </SpinnerWrapper>
                    )}
                    <ErrorText>{genError}</ErrorText>
                </div>
            )}
            {contractingParties.map(contractingParty => contractingParty.value)[0] && (
                <Title>CONTRACTING PARTIES</Title>
            )}
            {selectedContractLoadedState !== ContractsStatus.LOADED ? (
                <div>
                    <icons.LoadingSmallIcon data-testid="loading-contracting-party" />
                </div>
            ) : (
                <div data-testid="contracts-party-container">
                    {contractingParties
                        .map((contractingParty, index) => (
                            <PartyItem
                                key={`contract_${contractIndex}_contracting_party_${index}`}
                                data-testid={`contract_${contractIndex}_contracting_party_${index}`}
                            >
                                {contractingParty.value.trim()}
                            </PartyItem>
                        ))
                        .reduce(
                            (accu, elem) => (accu === null ? [elem] : [...accu, ',', elem]),
                            null
                        )}
                </div>
            )}
            <SingleSelectDropdown
                id="ddlDocType"
                label="DOWNLOAD OPTIONS"
                placeholder="Select file format"
                selectedValue={selectedValue}
                onChange={onChange}
                options={options}
                data-testid={`contract_${contractIndex}_download_options_dropdown`}
            />
            <ModalDialogFooter>
                <CancelButtonWrapper>
                    <buttons.Button
                        size="medium"
                        appearance="secondary"
                        data-testid={`contract_${contractIndex}_cancel_button`}
                        onClick={close}
                    >
                        Cancel
                    </buttons.Button>
                </CancelButtonWrapper>
                <DownoladButtonWrapper>
                    {browserDownload && (
                        <a
                            href={selectedItem === 'original' ? permaLinkOriginal : permaLinkDOCX}
                            rel="noopener noreferrer"
                            target="_blank"
                            download
                            onClick={consumeDownloadButtonLink}
                            data-testid={`contract_${contractIndex}_download_button`}
                        >
                            <buttons.Button
                                size="medium"
                                appearance="primary"
                                disabled={
                                    selectedContractLoadedState !== ContractsStatus.LOADED ||
                                    (selectedItem === 'docx' && isDOCXCopied) ||
                                    (selectedItem === 'original' && isOriginalCopied)
                                }
                            >
                                Download
                            </buttons.Button>
                        </a>
                    )}
                    {binaryDownload && (
                        <buttons.Button
                            size="medium"
                            appearance="primary"
                            disabled={isDisabled}
                            data-testid={`contract_${contractIndex}_download_button`}
                            onClick={downloadAndOpen}
                        >
                            {isDownloading ? 'Downloading...' : buttonText}
                        </buttons.Button>
                    )}
                    {showCopyToClipboardButton && (
                        <CopyToClipboard
                            text={selectedItem === 'original' ? permaLinkOriginal : permaLinkDOCX}
                        >
                            <buttons.Button
                                size="medium"
                                appearance="primary"
                                onClick={consumeClipboardLink}
                                disabled={
                                    selectedContractLoadedState !== ContractsStatus.LOADED ||
                                    (selectedItem === 'docx' && isDOCXCopied) ||
                                    (selectedItem === 'original' && isOriginalCopied)
                                }
                                data-testid={`contract_${contractIndex}_copy_to_clipboard_button`}
                            >
                                Copy link
                            </buttons.Button>
                        </CopyToClipboard>
                    )}
                </DownoladButtonWrapper>
                {!showCopyToClipboardButton && (
                    <LinkButtonWrapper>
                        <CopyToClipboard
                            text={selectedItem === 'original' ? permaLinkOriginal : permaLinkDOCX}
                        >
                            <div data-tip data-for="copy-download-link" data-place="bottom">
                                <buttons.Button
                                    size="medium"
                                    appearance="secondary"
                                    onClick={consumeClipboardLink}
                                    icon={<LinkIcon />}
                                    disabled={
                                        selectedContractLoadedState !== ContractsStatus.LOADED ||
                                        (selectedItem === 'docx' && isDOCXCopied) ||
                                        (selectedItem === 'original' && isOriginalCopied)
                                    }
                                    data-testid={`contract_${contractIndex}_copy_to_clipboard_button`}
                                />
                            </div>
                        </CopyToClipboard>
                    </LinkButtonWrapper>
                )}
                <ReactTooltip id="copy-download-link" effect="solid">
                    <span>Copy download link</span>
                </ReactTooltip>
            </ModalDialogFooter>
        </DownloadDialogWrapper>
    );
};

export default DownloadDialog;
